import { useToast } from '@medely/ui-kit';
import { useProNavigate } from '@mp/contexts';
import { useNetworkRequest } from '@mp/hooks/useNetworkRequest';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReleaseStatus } from './interface';

type ShiftRequestProps = {
  request_type: ReleaseStatus;
  requestee_id?: number;
  reason?: string;
  jobId: number;
};

type ShiftSwapCallbacks = {
  onAcceptSuccess?: () => void;
  onDeclineSuccess?: () => void;
  shouldNavigateToToday?: boolean;
};

export const useShiftSwapMutations = (callbacks?: ShiftSwapCallbacks) => {
  const { axios } = useNetworkRequest();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { navigate } = useProNavigate();

  const errorMessage = 'Sorry, something went wrong. Please try again.';

  const { mutate: declineSwapOffer, isPending: isDeclinePending } = useMutation(
    (id?: number | null) =>
      axios.post(`/v3/professional/shift_requests/${id}/decline`, {
        shift_request: {
          reason: '',
        },
      }),
    {
      onSuccess: () => {
        toast.success('Shift swap offer was declined.'),
          {
            name: 'check',
          };
        queryClient.invalidateQueries(['GetJobForJobDetailsContainer']);
        queryClient.invalidateQueries(['GetAvailableJobForAvailableJobDetailsContainer']);
        queryClient.invalidateQueries(['jobs']);
        queryClient.invalidateQueries({ queryKey: ['GetShiftSwaps'] });
        queryClient.invalidateQueries({ queryKey: ['GetProfessionalTodos'] });
        callbacks?.onDeclineSuccess?.();
        if (callbacks?.shouldNavigateToToday !== false) {
          navigate('Today');
        }
      },

      onError: () => {
        toast.error('Sorry, something went wrong. Please try again.');
      },
    },
  );

  const { mutate: acceptSwapOffer, isPending: isAcceptPending } = useMutation(
    (id?: number | null) => axios.post(`/v3/professional/shift_requests/${id}/accept`),
    {
      onSuccess: () => {
        toast.success(
          'Success! Your shift is pending approval and you will be notified when it is approved.',
        ),
          {
            name: 'check',
          };
        queryClient.invalidateQueries(['GetJobForJobDetailsContainer']);
        queryClient.invalidateQueries(['GetAvailableJobForAvailableJobDetailsContainer']);
        queryClient.invalidateQueries(['jobs']);
        queryClient.invalidateQueries({ queryKey: ['GetShiftSwaps'] });
        queryClient.invalidateQueries({ queryKey: ['GetProfessionalTodos'] });
        callbacks?.onAcceptSuccess?.();
        if (callbacks?.shouldNavigateToToday !== false) {
          navigate('Today');
        }
      },
      onError: () => {
        toast.error(errorMessage);
      },
    },
  );

  const { mutate: cancelShiftRequest, isPending: isCancelPending } = useMutation(
    ({ jobId, id }: { jobId: number; id: number }) =>
      axios.delete(`/v3/professional/jobs/${jobId}/shift_requests/${id}`),
    {
      onSuccess: () => {
        toast.success('Shift request was canceled.'),
          {
            name: 'check',
          };
        queryClient.invalidateQueries({ queryKey: ['GetJobForJobDetailsContainer'] });
        queryClient.invalidateQueries(['jobs']);
        navigate('Today');
      },
      onError: () => {
        toast.error(errorMessage);
      },
    },
  );

  const shiftReleaseSuccessMessage =
    'Success! Your shift is pending release and you will be notified when it is approved.';

  const shiftDropSuccessMessage =
    'Success! Your shift has been submitted for release. You’ll remain responsible until the facility approves.';

  const { mutate: addShiftRequest, isPending: isAddShiftRequestPending } = useMutation<
    unknown,
    unknown,
    ShiftRequestProps
  >(
    ({ request_type, requestee_id, reason, jobId }: ShiftRequestProps) =>
      axios.post(`/v3/professional/jobs/${jobId}/shift_requests`, {
        shift_request: {
          request_kind: request_type,
          requestee_id: request_type === 'swap' ? requestee_id : undefined,
          reason,
        },
      }),
    {
      onSuccess: (_data, variables) => {
        const { request_type } = variables;
        toast.success(
          request_type === 'drop' ? shiftDropSuccessMessage : shiftReleaseSuccessMessage,
        ),
          {
            name: 'check',
          };
        queryClient.invalidateQueries({ queryKey: ['GetJobForJobDetailsContainer'] });
        queryClient.invalidateQueries(['jobs']);
      },
      onError: () => {
        toast.error(errorMessage);
      },
    },
  );

  return {
    isLoading: isDeclinePending || isAcceptPending || isAddShiftRequestPending || isCancelPending,
    declineSwapOffer,
    acceptSwapOffer,
    addShiftRequest,
    cancelShiftRequest,
  };
};
